import { GameIDType } from '@/enums/gameId';
import { ModalsKey } from '@/enums/modalsKey';
import { GameReportType } from '@/hooks/gameReport/useGameReportSearchParams/gameReportType.enum';
import { useSearchParams } from '@/hooks/useSearchParams';
import { useMemo } from 'react';
import LastMatchesContent from '../../../LastMatches/Modal/Content';
import DateCard from '../../../LastMatches/Modal/Content/DateCard';
import LastRoundCard from './Card';

import { useGameRounds } from '@/api/game-round';
import NoDataFound from '@/components/Notifications/NotificationCenter/index.nodata';
import { ErrorCard } from '@starsoft/common/components';
import { parseToBigInt } from '@starsoft/common/utils/parsers';
import styles from './styles.module.scss';

export default function LastRoundsModalContent(): JSX.Element {
  const search = useSearchParams();
  const gameId = parseToBigInt(search.get('game-id')) as GameIDType;
  const {
    rounds,
    isPending,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
    refetch,
  } = useGameRounds({
    gameId,
    limit: 50,
  });

  const Rounds = useMemo(
    () =>
      rounds?.map(round => (
        <DateCard
          key={`last-rounds-modal-card-${round.id}`}
          createdAt={round?.createdAt}
        >
          <LastRoundCard
            gameId={gameId as GameIDType}
            round={round}
            modal={ModalsKey.GameReport}
            link={`?round-slug=${round?.slug}&round-id=${round?.id}&game-id=${gameId}&game-type=${GameReportType.RoundGame}`}
          />
        </DateCard>
      )),
    [rounds, gameId],
  );

  const Skeletons = useMemo(
    () =>
      Array.from({ length: 50 }).map((_, i) => (
        <DateCard key={`last-rounds-modal-card-skeleton-${i}`} isLoading>
          <LastRoundCard
            gameId={gameId as GameIDType}
            isLoading
            link=""
            modal={ModalsKey.GameReport}
          />
        </DateCard>
      )),
    [gameId],
  );

  if (error) {
    return <ErrorCard error={error} refetch={refetch} />;
  }

  if (rounds?.length == 0) {
    return (
      <div className={styles.container}>
        <NoDataFound />
      </div>
    );
  }

  return (
    <LastMatchesContent
      hasNextPage={hasNextPage}
      isLoading={isPending || isFetchingNextPage}
      loadMore={fetchNextPage}
    >
      {Rounds}
      {(isPending || isFetchingNextPage) && Skeletons}
    </LastMatchesContent>
  );
}
